<div class="combo-card" *ngIf="enabled">
  <!-- ── Playback pill (caption + pill) ───────────────────────────────── -->
  <div class="cc-playback">
    <!-- Dark caption bar -->
    <div class="cc-caption-row">
      <div class="cc-caption-chip">
        <span class="cc-caption-title">{{ captionTitle }}</span>
        <span class="cc-caption-dot">·</span>
        <span class="cc-caption-layer" [style.color]="accent">{{ shortName(state.config.layer) }}</span>
      </div>
      <button
        type="button"
        class="cc-collapse-btn"
        [appTourAnchor]="'filters.overlayCollapse'"
        (click)="toggleCollapse()"
        [attr.aria-label]="isCollapsed ? 'Expand controls' : 'Collapse controls'"
      >
        <svg *ngIf="!isCollapsed" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <!-- close_fullscreen (inward diagonals) -->
          <path d="M4 14h6v6M20 10h-6V4" stroke="#001A33" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M14 10l6-6M10 14l-6 6" stroke="#001A33" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <svg *ngIf="isCollapsed" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <!-- open_in_full (outward diagonals) -->
          <path d="M14 4h6v6M10 20H4v-6" stroke="#001A33" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M20 4l-7 7M4 20l7-7" stroke="#001A33" stroke-width="2" stroke-linecap="round"/>
        </svg>
      </button>
      <!-- Red ✕ — the primary way to dismiss the combo overlay (moved off the
           layer card, mirrors the app's caption-bar red close). -->
      <button
        type="button"
        class="cc-close-caption"
        [appTourAnchor]="'filters.overlayClose'"
        (click)="onClose()"
        aria-label="Close combo layer"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.4" stroke-linecap="round">
          <path d="M6 6l12 12M18 6L6 18"/>
        </svg>
      </button>
    </div>

    <!-- Light-glass pill -->
    <div class="cc-pill" *ngIf="!isCollapsed">
      <button
        type="button"
        class="cc-pill-btn cc-play"
        [appTourAnchor]="'filters.overlayPlay'"
        (click)="togglePlay()"
        [disabled]="!hasFrames"
        [style.color]="hasFrames ? accent : accent + '4D'"
        aria-label="Play / pause"
      >
        <svg *ngIf="!isPlaying" width="30" height="30" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
        <svg *ngIf="isPlaying" width="30" height="30" viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>
      </button>

      <ng-container *ngIf="!isWind">
        <span class="cc-v-divider"></span>
        <button type="button" class="cc-pill-btn cc-tune" (click)="openOptions()" aria-label="Display settings">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3C3C43" stroke-width="2" stroke-linecap="round">
            <path d="M4 6h10M18 6h2M4 12h2M10 12h10M4 18h6M14 18h6"/>
            <circle cx="16" cy="6" r="2" fill="#3C3C43" stroke="none"/>
            <circle cx="8" cy="12" r="2" fill="#3C3C43" stroke="none"/>
            <circle cx="12" cy="18" r="2" fill="#3C3C43" stroke="none"/>
          </svg>
        </button>
      </ng-container>

      <div class="cc-scrub-wrap">
        <input
          type="range"
          class="cc-scrub"
          [min]="hasFrames ? frameStart : 0"
          [max]="hasFrames ? frameLast : 1"
          [step]="1"
          [value]="sliderValue"
          [disabled]="!hasFrames"
          [style.--accent]="accent"
          (input)="onFrameScrub($any($event.target).valueAsNumber)"
          (change)="onFrameScrubEnd($any($event.target).valueAsNumber)"
          aria-label="Frame scrubber"
        />
      </div>
    </div>
  </div>

  <ng-container *ngIf="!isCollapsed">
    <!-- ── Gradient bar ────────────────────────────────────────────────── -->
    <div class="cc-gradient-card" [appTourAnchor]="'filters.overlayGradient'">
      <div class="cc-gradient-track">
        <div class="cc-gradient-fill" [style.background]="gradientCss(state.config.layer)"></div>

        <!-- Dual-thumb range (SST/CHL only). Custom pointer-driven control so
             (a) clicking anywhere on the bar jumps the nearest thumb to that
             spot (native <input type=range> doesn't do that on the track), and
             (b) the thumbs sit dead-centre on the gradient midline. -->
        <ng-container *ngIf="!isWind">
          <div
            class="cc-range"
            [class.disabled]="!rangeInteractive"
            (pointerdown)="onRangePointerDown($event)"
            (pointermove)="onRangePointerMove($event)"
            (pointerup)="onRangePointerUp($event)"
            (pointercancel)="onRangePointerUp($event)"
          >
            <div
              class="cc-range-thumb"
              [class.active]="rangeActiveThumb === 'min'"
              [style.left.%]="rangePct(cmin)"
              [attr.aria-label]="'Minimum colour range'"
              role="slider"
              [attr.aria-valuemin]="cfg.sliderMin"
              [attr.aria-valuemax]="cfg.sliderMax"
              [attr.aria-valuenow]="cmin"
            ></div>
            <div
              class="cc-range-thumb"
              [class.active]="rangeActiveThumb === 'max'"
              [style.left.%]="rangePct(cmax)"
              [attr.aria-label]="'Maximum colour range'"
              role="slider"
              [attr.aria-valuemin]="cfg.sliderMin"
              [attr.aria-valuemax]="cfg.sliderMax"
              [attr.aria-valuenow]="cmax"
            ></div>
          </div>
        </ng-container>
      </div>

      <div class="cc-gradient-labels">
        <ng-container *ngIf="!isWind">
          <span class="cc-legend-val">{{ cminLabel }}</span>
          <span class="cc-legend-val">{{ cmaxLabel }}</span>
        </ng-container>
        <ng-container *ngIf="isWind">
          <span class="cc-legend-val" *ngFor="let t of windTicks">{{ t }}</span>
        </ng-container>
      </div>

      <!-- ── Opacity — horizontal slider stacked under the gradient bar ──── -->
      <div class="cc-opacity-row" [appTourAnchor]="'filters.overlayOpacity'">
        <span class="cc-opacity-label">Opacity</span>
        <input
          type="range"
          class="cc-opacity-slider"
          [min]="10"
          [max]="100"
          [step]="5"
          [value]="opacityPct"
          [style.--accent]="accent"
          (input)="combo.setOpacity($any($event.target).valueAsNumber / 100)"
          aria-label="Overlay opacity"
        />
        <span class="cc-opacity-pct">{{ opacityPct }}%</span>
      </div>
    </div>
  </ng-container>

  <!-- ── ⚙ Options panel (no scrim) ─────────────────────────────────────── -->
  <div class="cc-options-sheet" *ngIf="showOptions" (click)="closeOptions()">
    <div class="cc-options-panel" (click)="$event.stopPropagation()">
      <div class="cc-options-grip"></div>

      <div class="cc-options-title-row">
        <div class="cc-options-icon" [style.background]="accent + '1F'">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" [attr.stroke]="accent" stroke-width="2" stroke-linecap="round">
            <path d="M4 6h10M18 6h2M4 12h2M10 12h10M4 18h6M14 18h6"/>
          </svg>
        </div>
        <span class="cc-options-title">Display settings</span>
        <button type="button" class="cc-options-done" [style.color]="accent" (click)="closeOptions()">Done</button>
      </div>

      <div class="cc-options-range-row">
        <span class="cc-options-range-label">Color range</span>
        <span class="cc-options-range-val">{{ rangeReadout }}</span>
      </div>
      <div class="cc-options-hint">
        {{ rangeEnabled ? 'Drag the gradient bar on the map control to adjust.' : 'Color range adjusts when the layer backend is live.' }}
      </div>
      <div class="cc-options-coverage" *ngIf="coverageLine">{{ coverageLine }}</div>

      <div class="cc-options-buttons">
        <button
          type="button"
          class="cc-btn-filled"
          [style.background]="stretchEnabled ? accent : accent + '4D'"
          [disabled]="!stretchEnabled"
          (click)="onAutoView()"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M13 2L3 14h7l-1 8 10-12h-7z"/></svg>
          Auto-view
        </button>
        <button
          type="button"
          class="cc-btn-outlined"
          [disabled]="!hasCustomRange"
          (click)="onReset()"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#001A33" stroke-width="2" stroke-linecap="round">
            <path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/>
          </svg>
          Reset
        </button>
      </div>

      <label class="cc-options-toggle" *ngIf="hasComposite">
        <span>Latest composite (gap-filled)</span>
        <input
          type="checkbox"
          [checked]="state.config.compositeChecked"
          [style.accent-color]="accent"
          (change)="onToggleComposite($any($event.target).checked)"
        />
      </label>
      <label class="cc-options-toggle" *ngIf="cfg.supportsFronts">
        <span>Thermal fronts</span>
        <input
          type="checkbox"
          [checked]="state.config.frontsOn"
          [style.accent-color]="accent"
          (change)="onToggleFronts()"
        />
      </label>
    </div>
  </div>
</div>
